import React, { useState, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
} from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { theme } from '../constants/theme';
import { SkeletonCompactList } from '../components/ShimmerLoading';
import ApiService from '../services/ApiService';
import { AuditLog, AuditAction } from '../models/types';
import { formatDate } from '../utils/helpers';

const ActivityLogScreen = ({ navigation }: any) => {
    const [logs, setLogs] = useState<AuditLog[]>([]);
    const [filterAction, setFilterAction] = useState<AuditAction | 'all'>('all');
    const [loading, setLoading] = useState(true);

    const loadLogs = async () => {
        try {
            setLoading(true);
            const allLogs = await ApiService.getAuditLogs(200);
            setLogs(allLogs);
        } catch (error) {
            console.error('Error loading audit logs:', error);
        } finally {
            setLoading(false);
        }
    };

    useFocusEffect(
        useCallback(() => {
            loadLogs();
        }, []),
    );

    const filteredLogs = filterAction === 'all'
        ? logs.filter(log => log.action !== 'create')
        : logs.filter(log => log.action === filterAction);

    const getActionIcon = (action: AuditAction) => {
        switch (action) {
            case 'create':
                return { name: 'plus-circle', color: '#4CAF50' };
            case 'update':
                return { name: 'pencil-circle', color: '#FF9800' };
            case 'delete':
                return { name: 'delete-circle', color: '#F44336' };
        }
    };

    const getTableLabel = (tableName: string) => {
        switch (tableName) {
            case 'cow_records':
                return 'Cow Record';
            case 'dairy_records':
                return 'Dairy Record';
            case 'cows':
                return 'Cow';
            case 'dairy_purchases':
                return 'Dairy Purchase';
            default:
                return tableName;
        }
    };

    const formatDateTime = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const formatFieldName = (field: string) => {
        // Special overrides only for things that auto-formatting can't handle perfectly (like acronyms)
        const overrides: Record<string, string> = {
            cowId: 'Cow ID',
            id: 'ID',
            userId: 'User ID',
            dob: 'Date of Birth',
        };

        if (overrides[field]) return overrides[field];

        // Dynamic formatting:
        // 1. Replace underscores with spaces (snake_case -> words)
        // 2. Add space before capital letters (camelCase -> words)
        // 3. Capitalize first letter of each word
        return field
            // Handle snake_case: replace _ with space
            .replace(/_/g, ' ')
            // Handle camelCase: insert space before capital letters
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            // Capitalize first letter of every word
            .replace(/\b\w/g, char => char.toUpperCase())
            .trim();
    };

    const renderDeletedData = (changesJson: string | undefined) => {
        if (!changesJson) return null;
        try {
            const changes = JSON.parse(changesJson);
            const { recordType, deletedData } = changes;

            if (!deletedData) return null;

            return (
                <View style={styles.deletedDataContainer}>
                    <Text style={styles.deletedDataTitle}>Deleted {recordType} record:</Text>
                    {Object.entries(deletedData).map(([key, value], index) => (
                        value !== null && value !== undefined && (
                            <View key={index} style={styles.deletedDataRow}>
                                <Text style={styles.deletedDataKey}>{formatFieldName(key)}:</Text>
                                <Text style={styles.deletedDataValue}>{String(value)}</Text>
                            </View>
                        )
                    ))}
                </View>
            );
        } catch {
            return null;
        }
    };

    const renderChanges = (changesJson: string | undefined) => {
        if (!changesJson) return null;
        try {
            const changes = JSON.parse(changesJson);
            const { recordType, before, after } = changes;

            // Find what changed
            const changedFields: string[] = [];
            if (before && after) {
                Object.keys(after).forEach(key => {
                    if (before[key] !== after[key]) {
                        changedFields.push(key);
                    }
                });
            }
            if (changedFields.length === 0) return null;

            return (
                <View style={styles.changesContainer}>
                    <Text style={styles.changesTitle}>Changes ({recordType || 'record'}):</Text>
                    {changedFields.map((field, index) => (
                        <View key={index} style={styles.changeRow}>
                            <Text style={styles.fieldName}>{formatFieldName(field)}:</Text>
                            <Text style={styles.oldValue}>{before[field] ?? 'N/A'}</Text>
                            <Icon name="arrow-right" size={12} color={theme.colors.textSecondary} />
                            <Text style={styles.newValue}>{after[field] ?? 'N/A'}</Text>
                        </View>
                    ))}
                </View>
            );
        } catch {
            return null;
        }
    };

    const renderLogItem = ({ item }: { item: AuditLog }) => {
        const icon = getActionIcon(item.action);
        const isDelete = item.action === 'delete';

        return (
            <View style={[styles.logCard, isDelete && styles.logCard_deleted]}>
                <View style={[styles.iconContainer, { backgroundColor: icon.color + '20' }]}>
                    <Icon name={icon.name} size={24} color={icon.color} />
                </View>
                <View style={styles.logContent}>
                    <View style={styles.logHeader}>
                        <Text style={[styles.actionBadge, { backgroundColor: icon.color + '20', color: icon.color }]}>
                            {item.action.toUpperCase()}
                        </Text>
                        <Text style={styles.logTime}>{formatDateTime(item.createdAt)}</Text>
                    </View>
                    <Text style={styles.logDescription}>
                        <Text style={styles.userName}>{item.userName}</Text>
                        {' '}{item.action === 'delete' ? 'deleted' : item.action === 'update' ? 'updated' : 'created'}{' '}
                        a {getTableLabel(item.tableName)}
                    </Text>
                    {/* Record ID hidden as requested */}

                    {item.action === 'update' && renderChanges(item.changes)}

                    {isDelete && renderDeletedData(item.changes)}

                    {isDelete && (
                        <View style={styles.deleteWarning}>
                            <Icon name="alert-circle" size={14} color="#F44336" />
                            <Text style={styles.deleteWarningText}>Removed from calculations</Text>
                        </View>
                    )}
                </View>
            </View>
        );
    };

    const renderEmpty = () => (
        <View style={styles.emptyContainer}>
            <Icon name="clipboard-text-clock" size={80} color={theme.colors.textLight} />
            <Text style={styles.emptyText}>No activity yet</Text>
            <Text style={styles.emptySubtext}>All edits and deletions will appear here</Text>
        </View>
    );

    return (
        <SafeAreaView style={styles.container}>
            <View style={styles.header}>
                <TouchableOpacity
                    style={styles.backButton}
                    onPress={() => navigation.goBack()}
                >
                    <Icon name="arrow-left" size={24} color={theme.colors.surface} />
                </TouchableOpacity>
                <Text style={styles.title}>Activity Log</Text>
                <View style={styles.placeholder} />
            </View>

            <View style={styles.filterContainer}>
                {(['all', 'update', 'delete'] as const).map(action => (
                    <TouchableOpacity
                        key={action}
                        style={[
                            styles.filterButton,
                            filterAction === action && styles.filterButton_active,
                        ]}
                        onPress={() => setFilterAction(action)}
                    >
                        <Text
                            style={[
                                styles.filterText,
                                filterAction === action && styles.filterText_active,
                            ]}
                        >
                            {action.charAt(0).toUpperCase() + action.slice(1)}
                        </Text>
                    </TouchableOpacity>
                ))}
            </View>

            <FlashList
                data={filteredLogs}
                renderItem={renderLogItem}
                keyExtractor={item => item.id}
                contentContainerStyle={styles.listContainer}
                ListEmptyComponent={loading ? <SkeletonCompactList itemCount={8} /> : renderEmpty}
                showsVerticalScrollIndicator={false}
            />
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    header: {
        backgroundColor: theme.colors.primary,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: theme.spacing.sm,
        paddingHorizontal: theme.spacing.md,
        ...theme.shadows.sm,
    },
    backButton: {
        padding: theme.spacing.xs,
    },
    title: {
        ...theme.typography.h5,
        color: theme.colors.surface,
        fontWeight: '700',
    },
    placeholder: {
        width: 32,
    },
    filterContainer: {
        flexDirection: 'row',
        padding: theme.spacing.md,
        gap: theme.spacing.sm,
    },
    filterButton: {
        flex: 1,
        paddingVertical: theme.spacing.sm,
        alignItems: 'center',
        borderRadius: theme.borderRadius.md,
        backgroundColor: theme.colors.surface,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    filterButton_active: {
        backgroundColor: theme.colors.primary,
        borderColor: theme.colors.primary,
    },
    filterText: {
        ...theme.typography.caption,
        color: theme.colors.text,
        fontWeight: '600',
    },
    filterText_active: {
        color: theme.colors.surface,
    },
    listContainer: {
        padding: theme.spacing.md,
        paddingTop: 0,
    },
    logCard: {
        flexDirection: 'row',
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.md,
        ...theme.shadows.sm,
    },
    iconContainer: {
        width: 48,
        height: 48,
        borderRadius: 24,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: theme.spacing.md,
    },
    logContent: {
        flex: 1,
    },
    logHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing.xs,
    },
    actionBadge: {
        ...theme.typography.caption,
        fontWeight: '700',
        paddingHorizontal: theme.spacing.sm,
        paddingVertical: 2,
        borderRadius: theme.borderRadius.sm,
        overflow: 'hidden',
    },
    logTime: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
    },
    logDescription: {
        ...theme.typography.body2,
        color: theme.colors.text,
        marginBottom: theme.spacing.xs,
    },
    userName: {
        fontWeight: '600',
        color: theme.colors.primary,
    },
    recordId: {
        ...theme.typography.caption,
        color: theme.colors.textLight,
    },
    emptyContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: theme.spacing.xxl * 2,
    },
    emptyText: {
        ...theme.typography.h5,
        color: theme.colors.textSecondary,
        marginTop: theme.spacing.md,
    },
    emptySubtext: {
        ...theme.typography.body2,
        color: theme.colors.textLight,
        marginTop: theme.spacing.xs,
    },
    logCard_deleted: {
        borderLeftWidth: 4,
        borderLeftColor: '#F44336',
    },
    changesContainer: {
        marginTop: theme.spacing.sm,
        backgroundColor: '#FFF8E1',
        padding: theme.spacing.sm,
        borderRadius: theme.borderRadius.sm,
    },
    changesTitle: {
        ...theme.typography.caption,
        color: '#FF9800',
        fontWeight: '700',
        marginBottom: theme.spacing.xs,
    },
    changeRow: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        marginBottom: 2,
    },
    fieldName: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginRight: theme.spacing.xs,
    },
    oldValue: {
        ...theme.typography.caption,
        color: '#F44336',
        textDecorationLine: 'line-through',
        marginRight: theme.spacing.xs,
    },
    newValue: {
        ...theme.typography.caption,
        color: '#4CAF50',
        fontWeight: '600',
        marginLeft: theme.spacing.xs,
    },
    deleteWarning: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: theme.spacing.sm,
        backgroundColor: '#FFEBEE',
        padding: theme.spacing.sm,
        borderRadius: theme.borderRadius.sm,
    },
    deleteWarningText: {
        ...theme.typography.caption,
        color: '#F44336',
        marginLeft: theme.spacing.xs,
        flex: 1,
    },
    deletedDataContainer: {
        marginTop: theme.spacing.sm,
        backgroundColor: '#FFEBEE',
        padding: theme.spacing.sm,
        borderRadius: theme.borderRadius.sm,
        borderLeftWidth: 3,
        borderLeftColor: '#F44336',
    },
    deletedDataTitle: {
        ...theme.typography.caption,
        color: '#F44336',
        fontWeight: '700',
        marginBottom: theme.spacing.xs,
    },
    deletedDataRow: {
        flexDirection: 'row',
        marginBottom: 2,
    },
    deletedDataKey: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginRight: theme.spacing.xs,
    },
    deletedDataValue: {
        ...theme.typography.caption,
        color: theme.colors.text,
        fontWeight: '500',
    },
});

export default ActivityLogScreen;
